import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button, Select } from "../../../shared/ui";
import { listAdminPlans, setWorkspaceBilling, type AdminPlan } from "../../../features/admin/api/adminApi";

// Cache plans across editors so each row doesn't refetch.
let plansCache: AdminPlan[] | null = null;

/** Drop the cached plan list (call after plans are created/edited). */
export function invalidatePlansCache() {
  plansCache = null;
}

/** Inline editor to set a workspace's billing mode + manual plan assignment. */
export default function WorkspaceBillingEditor({
  workspaceId,
  billingType,
  planId,
  manualPlanEndsAt,
  onSaved,
}: {
  workspaceId: string;
  billingType: "stripe" | "manual";
  planId: string | null;
  manualPlanEndsAt: string | null;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState<AdminPlan[]>(plansCache ?? []);
  const [type, setType] = useState<"stripe" | "manual">(billingType);
  const [selectedPlan, setSelectedPlan] = useState(planId ?? "");
  const [endsAt, setEndsAt] = useState(manualPlanEndsAt ? manualPlanEndsAt.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || plansCache) return;
    listAdminPlans().then((p) => { plansCache = p; setPlans(p); }).catch(() => undefined);
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await setWorkspaceBilling(workspaceId, {
        billingType: type,
        manualPlanId: type === "manual" ? (selectedPlan || null) : undefined,
        manualPlanEndsAt: type === "manual" ? (endsAt || null) : undefined,
      });
      setOpen(false);
      onSaved();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to update billing");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-[12px] font-medium text-gold-600 hover:text-gold-700">
        Manage billing & plan
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-ink-100 bg-canvas/40 p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Select
          label="Billing mode"
          value={type}
          onChange={(e) => setType(e.target.value as "stripe" | "manual")}
          options={[
            { value: "stripe", label: "Stripe (self-serve)" },
            { value: "manual", label: "Manual (comped/invoiced)" },
          ]}
        />
        {type === "manual" && (
          <>
            <Select
              label="Plan"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              options={[{ value: "", label: "No plan" }, ...plans.filter((p) => p.status !== "archived").map((p) => ({ value: p._id, label: p.name }))]}
            />
            <div>
              <label className="mb-1 block text-[11px] font-medium text-ink-500">Plan ends (optional)</label>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
              />
            </div>
          </>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="primary" size="sm" disabled={saving} onClick={save}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : null} Save
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}
